/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from 'assets/close-icon.svg';
import i18nContext from 'components/i18n-context';
import { CONFIRMATION_HANDLE_TYPES, SMS_ERROR_CODE_DISABLE, TWO_FACTOR_AUTH_TYPES } from 'components/common/constants';
import SwitchConfirmationMethodScreen, {
  getUnavailableConfirmationTypes,
  hasSwitchableConfirmationMethod
} from 'components/common/ActionConfirmation/components/SwitchConfirmationMethodScreen';
import { ConfirmCodeWrapper } from 'components/common/ConfirmCodeWrapper/ConfirmCodeWrapper';
import { isFullSecurityCode } from 'components/utils';
import {
  getAvailableConfirmationTypes,
  getConfirmationActionType,
  getConfirmationDestination
} from 'services/authUtils';
import { getErrorMessageForAlert } from 'services/utils';
import Button from 'uikit/Button/Button';
import './PopUpScheme.scss';

const getTransferDetailsFields = (i18n, transferData, isCryptoTransaction = false) => {
  return [
    {
      label: i18n.getMessage('currencyExchange.form.from'),
      value: transferData?.accountFrom?.iban?.iban || transferData?.accountFrom?.multi_iban?.iban
    },
    {
      label: i18n.getMessage('currencyExchange.form.sentAmount'),
      value: transferData?.amountFromDetails?.displayAmount || transferData?.amountFrom
    },
    {
      label: i18n.getMessage('currencyExchange.form.currency'),
      value: transferData?.amountFromDetails?.currency || transferData?.accountFrom?.currency
    },
    {
      label: i18n.getMessage('currencyExchange.form.to'),
      value: transferData?.accountTo?.iban?.iban || transferData?.accountTo?.multi_iban?.iban
    },
    {
      label: i18n.getMessage('currencyExchange.form.receivedAmount'),
      value: transferData?.amountToDetails?.displayAmount || transferData?.amountTo
    },
    {
      label: i18n.getMessage('currencyExchange.form.currency'),
      value: transferData?.amountToDetails?.currency || transferData?.accountTo?.currency
    },
    {
      label: i18n.getMessage('currencyExchange.form.yourRate'),
      // eslint-disable-next-line max-len
      value: `${transferData?.rateDetails?.displayRate || transferData?.rate} ${transferData?.accountFrom?.currency}/${transferData?.accountTo?.currency}`
    },
    {
      label: i18n.getMessage('transfer.confirmation.label.commission'),
      // eslint-disable-next-line max-len
      value: `${transferData?.commissionDetails?.displayAmount}`
    }
  ].filter((field) => field.value || field.value === 0);
};

const PopUpCurrencyExchangePaymentConfirmationScheme = ({
  isLoading,
  transferData,
  onClose,
  onConfirm,
  error,
  clearError,
  isCurrency,
  isCryptoTransaction,
  generateSecurityCode,
  resendSecurityCode,
  isShowSwitchConfirmationMethodScreen,
  setIsShowSwitchConfirmationMethodScreen,
  handleForceSwitchToAnotherConfirmationType,
  currentConfirmationType
}) => {
  const i18n = useContext(i18nContext);
  const [alertState, setAlertState] = useState({ type: '', message: '' });
  const [showCountDown, setShowCountDown] = useState(false);
  const [securityCode, setSecurityCode] = useState('');
  const [showConfirmationCode, setShowConfirmationCode] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);
  const pushPollingIntervalRef = useRef(null);

  useEffect(() => {
    if (isCurrency && !showConfirmationCode) {
      handleConfirm();
    }
  }, []);

  useEffect(() => {
    if (SMS_ERROR_CODE_DISABLE.includes(error?.code)) {
      setIsDisabled(true);
    }
    // Don't show alert for CONFIRMATION_PENDING_ACTION - it's normal during push notification polling
    if (error && error?.code !== 'CONFIRMATION_PENDING_ACTION') {
      setAlertState({ type: 'warning', message: getErrorMessageForAlert(i18n, error) });
      setIsConfirmDisabled(true);
    } else if (!error) {
      setAlertState({ type: '', message: '' });
      setIsDisabled(false);
    }
  }, [error]);

  useEffect(() => {
    // Reset security code when confirmation type changes
    setSecurityCode('');
    setIsConfirmDisabled(true);

    if (currentConfirmationType !== TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH) {
      setShowCountDown(true);
    }
    // Don't show "code sent" message for Currency Exchange or push notifications
    if (!isCurrency && currentConfirmationType !== TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION) {
      setAlertState({ type: 'success', message: i18n.getMessage('pin.alert.sent') });
    }
    return () => {
      clearError();
    };
  }, [currentConfirmationType, isCurrency]);

  // Start polling for push notification confirmation
  useEffect(() => {
    if (currentConfirmationType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION && isCurrency) {
      pushPollingIntervalRef.current = setInterval(() => {
        // Call onConfirm with null code for push notification polling
        onConfirm(null);
      }, 2000); // Poll every 2 seconds
    }

    return () => {
      if (pushPollingIntervalRef.current) {
        clearInterval(pushPollingIntervalRef.current);
      }
    };
  }, [currentConfirmationType, isCurrency, onConfirm]);

  const handleResendCode = async () => {
    clearError();
    setShowCountDown(true);
    isDisabled && setIsDisabled(false);
    setAlertState({ type: 'success', message: i18n.getMessage('pin.alert.sent') });
    await resendSecurityCode();
  };

  const onTimerEnd = () => {
    setShowCountDown(false);
  };

  const onComplete = (code) => {
    setSecurityCode(code);
    if (!isFullSecurityCode(code)) {
      setIsConfirmDisabled(true);
    } else {
      setIsConfirmDisabled(false);
      setAlertState({ type: '', message: '' });
    }
  };

  const handleConfirm = () => {
    if (!showConfirmationCode) {
      setShowConfirmationCode(true);
      if (getConfirmationActionType()) {
        setIsConfirmDisabled(true);
        if (!isCurrency) {
          generateSecurityCode();
        }
      }
      return;
    }
    onConfirm(securityCode);
  };

  const handleEdit = () => {
    if (showConfirmationCode) {
      setShowConfirmationCode(false);
      return;
    }
    onClose();
  };

  const availableConfirmationTypes = getAvailableConfirmationTypes();
  const unavailableConfirmationTypes = getUnavailableConfirmationTypes(error);

  const schemeLine = (label, value, index) => {
    return (
      <div key={`${label}-${index}`} className={'scheme-line'}>
        <p className={'left'}>{i18n.getMessage(label) + ':'}</p>
        <p className={'center'}>{value}</p>
      </div>
    );
  };

  return (
    <div className={'scheme-wrapper transfer-confirmation'}>
      <div className={'scheme-header'}>
        <p className={'account'}>{i18n.getMessage('popUp.paymentConfirmation')}</p>
        <CloseIcon className={'scheme-close-icon'} onClick={onClose} />
      </div>
      <div className={'scheme-line-wrapper'}>
        {getTransferDetailsFields(i18n, transferData, isCryptoTransaction).map((field, index) =>
          schemeLine(field.label, field.value, index)
        )}
      </div>
      {isShowSwitchConfirmationMethodScreen ? (
        <SwitchConfirmationMethodScreen
          onSwitchMethod={(confirmationType) => {
            // Clear the security code and disable confirm button when switching methods
            setSecurityCode('');
            setIsConfirmDisabled(true);
            handleForceSwitchToAnotherConfirmationType(confirmationType);
          }}
          onBack={() => {
            // Clear the security code when going back without switching
            setSecurityCode('');
            setIsConfirmDisabled(true);
            setIsShowSwitchConfirmationMethodScreen(false);
          }}
          currentConfirmationType={currentConfirmationType}
          availableConfirmationTypes={availableConfirmationTypes}
          unavailableConfirmationTypes={unavailableConfirmationTypes}
        />
      ) : (
        <>
          {showConfirmationCode && (
            <ConfirmCodeWrapper
              PhoneComponent={<strong>{getConfirmationDestination(currentConfirmationType)}</strong>}
              isDisabled={isDisabled}
              error={!error?.type && error}
              onComplete={onComplete}
              showCountDown={showCountDown}
              handleResendCode={handleResendCode}
              onTimerEnd={onTimerEnd}
              alertState={alertState}
              confirmationHandleType={CONFIRMATION_HANDLE_TYPES.ON_CHANGE}
              handleSwitchToAnotherMethod={() => {
                // Clear the code when opening switch method screen
                setSecurityCode('');
                setIsConfirmDisabled(true);
                setIsShowSwitchConfirmationMethodScreen(true);
              }}
              isSwitchToAnotherConfirmationMethod={hasSwitchableConfirmationMethod(
                currentConfirmationType,
                availableConfirmationTypes,
                unavailableConfirmationTypes
              )}
            />
          )}
          <div className={`buttons-wrapper ${showConfirmationCode ? 'single' : ''}`}>
            {!showConfirmationCode && (
              <Button type={'outline'} size={'large'} onClick={handleEdit}>
                {i18n.getMessage('transferConfirmation.button.editTransfer')}
              </Button>
            )}
            <Button
              type={'primary'}
              size={'large'}
              roleType={'submit'}
              isDisabled={
                isLoading ||
                isConfirmDisabled ||
                ['CONFIRMATION_CODE_IS_OUT_OF_DATE', 'CONFIRMATION_CODE_TRIES_EXCEED'].includes(error?.code)
              }
              onClick={handleConfirm}
            >
              {i18n.getMessage('transferConfirmation.button.confirm')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

PopUpCurrencyExchangePaymentConfirmationScheme.propTypes = {
  isLoading: PropTypes.bool,
  transferData: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  error: PropTypes.any,
  isCurrency: PropTypes.bool,
  isCryptoTransaction: PropTypes.bool,
  generateSecurityCode: PropTypes.func,
  resendSecurityCode: PropTypes.func.isRequired,
  isShowSwitchConfirmationMethodScreen: PropTypes.bool,
  setIsShowSwitchConfirmationMethodScreen: PropTypes.func,
  handleForceSwitchToAnotherConfirmationType: PropTypes.func,
  currentConfirmationType: PropTypes.string
};

export default PopUpCurrencyExchangePaymentConfirmationScheme;
